import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Loader2, UserPlus, UserX, Search, Mail, Phone } from 'lucide-react';
import { searchUsers } from '@/lib/firebase/queries/organizations/searchUsers';
import { getOrganizationInstructors } from '@/lib/firebase/queries/organizations/instructors';
import { addInstructorToOrganization, removeInstructorFromOrganization } from '@/lib/firebase/queries/organizations/instructors';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Organization } from '@/types/organization';
import type { User } from '@/types';
import { toast } from 'sonner';

interface OrganizationTeachersProps {
  organization: Organization | null;
  onSuccess?: () => void;
}

export function OrganizationTeachers({ organization, onSuccess }: OrganizationTeachersProps) {
  const [availableInstructors, setAvailableInstructors] = useState<User[]>([]);
  const [currentInstructors, setCurrentInstructors] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [availableSearchQuery, setAvailableSearchQuery] = useState('');
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const { currentUser } = useAuth();
  const { t, dir } = useLanguage();
  const isRTL = dir === 'rtl';

  useEffect(() => {
    if (organization) {
      loadInstructors();
    }
  }, [organization]);

  const filterInstructors = (instructors: User[], query: string) => {
    if (!query.trim()) return instructors;
    
    const searchTerm = query.toLowerCase();
    return instructors.filter(instructor => {
      // Optional-chained: searchUsers casts a raw doc.data() spread to User, so a
      // user document with no email arrives as undefined and crashed the page.
      const nameEn = `${instructor.firstNameEn ?? ''} ${instructor.lastNameEn ?? ''}`.toLowerCase();
      const nameAr = `${instructor.firstNameAr ?? ''} ${instructor.lastNameAr ?? ''}`.toLowerCase();
      const email = instructor.email?.toLowerCase() || '';
      const phone = instructor.phone?.toLowerCase() || '';
      
      return nameEn.includes(searchTerm) || 
             nameAr.includes(searchTerm) || 
             email.includes(searchTerm) || 
             phone.includes(searchTerm);
    });
  };

  const filteredAvailableInstructors = filterInstructors(availableInstructors, availableSearchQuery);
  const filteredCurrentInstructors = filterInstructors(currentInstructors, currentSearchQuery);

  const loadInstructors = async () => {
    if (!organization) return;
    
    try {
      setLoading(true);
      const [available, current] = await Promise.all([
        searchUsers('instructor'),
        getOrganizationInstructors(organization.id)
      ]);

      // Filter out instructors that are already assigned
      const currentIds = new Set(current.map(i => i.id));
      setAvailableInstructors(available.filter(i => !currentIds.has(i.id)));
      setCurrentInstructors(current);
    } catch (error) {
      console.error('Error loading instructors:', error);
      toast.error('Failed to load instructors');
    } finally {
      setLoading(false);
    }
  };

  const handleAddInstructor = async (instructor: User) => {
    if (!organization || !currentUser) return;

    try {
      setProcessingId(instructor.id);
      await addInstructorToOrganization(organization.id, instructor.id, currentUser.uid);
      
      // Move instructor from available to current
      setAvailableInstructors(prev => prev.filter(i => i.id !== instructor.id));
      setCurrentInstructors(prev => [...prev, instructor]);
      
      toast.success(`${instructor.firstNameEn} added as instructor`);
      onSuccess?.();
    } catch (error) {
      console.error('Error adding instructor:', error);
      toast.error('Failed to add instructor');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRemoveInstructor = async (instructor: User) => {
    if (!organization) return;

    try {
      setProcessingId(instructor.id);
      await removeInstructorFromOrganization(organization.id, instructor.id);
      
      // Move instructor from current to available
      setCurrentInstructors(prev => prev.filter(i => i.id !== instructor.id));
      setAvailableInstructors(prev => [...prev, instructor]);
      
      toast.success(`${instructor.firstNameEn} removed from instructors`);
      onSuccess?.();
    } catch (error) {
      console.error('Error removing instructor:', error);
      toast.error('Failed to remove instructor');
    } finally {
      setProcessingId(null);
    }
  };

  if (!organization) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">
          Please select an organization to manage instructors.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span>Loading instructors...</span>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-6" dir={dir}>
      {/* Current Instructors */}
      <div className="space-y-4">
        <h3 className="font-medium">{t('dashboard.admin.organizations.members.currentInstructors')}</h3>
        
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={dir === 'rtl' ? 'البحث في المحاضرين الحاليين...' : 'Search current instructors...'}
            value={currentSearchQuery}
            onChange={(e) => setCurrentSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <ScrollArea className="h-[400px]">
          <div className="space-y-2 px-4 pb-4">
            {filteredCurrentInstructors.map((instructor) => (
              <div
                key={instructor.id}
                className="flex items-center gap-3 p-3 rounded-lg border"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    <span className="truncate max-w-[166px]">{instructor.email}</span>
                  </div>
                  {instructor.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      <span className="truncate max-w-[100px]">{instructor.phone}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">
                    {dir === 'rtl' 
                      ? `${instructor.firstNameAr} ${instructor.lastNameAr}`
                      : `${instructor.firstNameEn} ${instructor.lastNameEn}`
                    }
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveInstructor(instructor)}
                  disabled={!!processingId}
                  className="flex-shrink-0 bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700"
                >
                  {processingId === instructor.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UserX className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
            {filteredCurrentInstructors.length === 0 && (
              <div className="text-center py-4 text-muted-foreground">
                {currentSearchQuery ? 
                  (dir === 'rtl' ? 'لا توجد نتائج للبحث' : 'No search results') :
                  t('dashboard.admin.organizations.members.noInstructorsAssigned')
                }
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Available Instructors */}
      <div className="space-y-4">
        <h3 className="font-medium">{t('dashboard.admin.organizations.members.availableInstructors')}</h3>
        
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={dir === 'rtl' ? 'البحث في المحاضرين المتاحين...' : 'Search available instructors...'}
            value={availableSearchQuery}
            onChange={(e) => setAvailableSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <ScrollArea className="h-[400px]">
          <div className="space-y-2 px-4 pb-4">
            {filteredAvailableInstructors.map((instructor) => (
              <div
                key={instructor.id}
                className="flex items-center gap-3 p-3 rounded-lg border"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    <span className="truncate max-w-[166px]">{instructor.email}</span>
                  </div>
                  {instructor.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      <span className="truncate max-w-[100px]">{instructor.phone}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">
                    {dir === 'rtl' 
                      ? `${instructor.firstNameAr} ${instructor.lastNameAr}`
                      : `${instructor.firstNameEn} ${instructor.lastNameEn}`
                    }
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleAddInstructor(instructor)}
                  disabled={!!processingId}
                  className="flex-shrink-0 bg-green-50 hover:bg-green-100 text-green-600 hover:text-green-700"
                >
                  {processingId === instructor.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UserPlus className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
            {filteredAvailableInstructors.length === 0 && (
              <div className="text-center py-4 text-muted-foreground">
                {availableSearchQuery ? 
                  (dir === 'rtl' ? 'لا توجد نتائج للبحث' : 'No search results') :
                  t('dashboard.admin.organizations.members.noInstructorsAvailable')
                }
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}